(() => {
  const MOBILE_QUERY = '(max-width: 820px)';

  const parseVariants = (root) => {
    const node = root.querySelector('[data-fbp-variants]');
    if (!node) return [];

    try {
      return JSON.parse(node.textContent);
    } catch (_error) {
      return [];
    }
  };

  const initProduct = (root) => {
    if (!root || root.dataset.fbpInitialized === 'true') return;
    root.dataset.fbpInitialized = 'true';

    const variants = parseVariants(root);
    const form = root.querySelector('.fbp-form');
    const variantInput = root.querySelector('[data-fbp-variant-id]');
    const optionGroups = Array.from(root.querySelectorAll('[data-fbp-option]'));
    const price = root.querySelector('[data-fbp-price]');
    const comparePrice = root.querySelector('[data-fbp-compare-price]');
    const availability = root.querySelector('[data-fbp-availability]');
    const availabilityLabel = root.querySelector('[data-fbp-availability-label]');
    const addButton = root.querySelector('[data-fbp-add]');
    const addLabel = root.querySelector('[data-fbp-add-label]');
    const mobileBuy = root.querySelector('[data-fbp-mobile-buy]');
    const mobilePrice = root.querySelector('[data-fbp-mobile-price]');
    const mobileButton = root.querySelector('[data-fbp-mobile-submit]');
    const mobileLabel = root.querySelector('[data-fbp-mobile-label]');
    const gallery = root.querySelector('[data-fbp-gallery]');
    const mediaItems = Array.from(root.querySelectorAll('[data-fbp-media]'));
    const mediaButtons = Array.from(root.querySelectorAll('[data-fbp-media-button]'));

    const selectedOptions = () =>
      optionGroups.map((group) => group.querySelector('input:checked')?.value || '');

    const findVariant = () => {
      if (optionGroups.length === 0) {
        const currentId = Number(variantInput?.value);
        return variants.find((variant) => Number(variant.id) === currentId) || variants[0];
      }

      const selected = selectedOptions();
      return variants.find(
        (variant) =>
          variant.options.length === selected.length &&
          variant.options.every((option, index) => option === selected[index])
      );
    };

    const setActiveMediaButton = (mediaId) => {
      mediaButtons.forEach((button) => {
        const active = String(button.dataset.mediaId) === String(mediaId);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };

    const scrollToMedia = (mediaId, behavior = 'smooth') => {
      if (!gallery || !window.matchMedia(MOBILE_QUERY).matches) return;
      const media = mediaItems.find((item) => String(item.dataset.mediaId) === String(mediaId));
      if (!media) return;

      gallery.scrollTo({
        left: Math.max(0, media.offsetLeft - 20),
        behavior
      });
      setActiveMediaButton(mediaId);
    };

    const updateOptionLegends = () => {
      optionGroups.forEach((group) => {
        const output = group.querySelector('[data-fbp-option-value]');
        const selected = group.querySelector('input:checked');
        if (output && selected) output.textContent = selected.value;
      });
    };

    const updateVariant = ({ updateUrl = true, moveMedia = true } = {}) => {
      const variant = findVariant();
      updateOptionLegends();

      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (mobileButton) mobileButton.disabled = true;
        if (availability) {
          availability.classList.add('is-unavailable');
          if (availabilityLabel) {
            availabilityLabel.textContent = availability.dataset.unavailableLabel || 'Unavailable';
          }
        }
        return;
      }

      const isAvailable = Boolean(variant.available);
      if (variantInput) variantInput.value = variant.id;
      if (price) price.textContent = variant.price;
      if (mobilePrice) mobilePrice.textContent = variant.price;

      if (comparePrice) {
        comparePrice.hidden = !variant.compareAt;
        comparePrice.textContent = variant.compareAt || '';
      }

      if (availability) {
        availability.classList.toggle('is-unavailable', !isAvailable);
        if (availabilityLabel) {
          availabilityLabel.textContent = isAvailable
            ? availability.dataset.availableLabel || 'Available'
            : availability.dataset.unavailableLabel || 'Unavailable';
        }
      }

      if (addButton) {
        addButton.disabled = !isAvailable;
        if (addLabel) {
          addLabel.textContent = isAvailable
            ? addButton.dataset.availableLabel || 'ADD TO BAG'
            : addButton.dataset.soldOutLabel || 'SOLD OUT';
        }
      }

      if (mobileButton) {
        mobileButton.disabled = !isAvailable;
        if (mobileLabel) {
          mobileLabel.textContent = isAvailable
            ? mobileButton.dataset.availableLabel || 'ADD'
            : mobileButton.dataset.soldOutLabel || 'SOLD OUT';
        }
      }

      if (updateUrl && variant.url && window.history?.replaceState) {
        window.history.replaceState({}, '', variant.url);
      }

      if (moveMedia && variant.mediaId) {
        scrollToMedia(variant.mediaId);
      }
    };

    optionGroups.forEach((group) => {
      group.addEventListener('change', () => updateVariant());
    });

    const quantity = root.querySelector('[data-fbp-quantity]');
    root.querySelector('[data-fbp-quantity-minus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = Math.max(Number(quantity.min || 1), Number(quantity.value || 1) - 1);
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    });

    root.querySelector('[data-fbp-quantity-plus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = Math.max(Number(quantity.min || 1), Number(quantity.value || 1) + 1);
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    });

    if (mobileButton && form) {
      mobileButton.addEventListener('click', () => form.requestSubmit());
    }

    mediaButtons.forEach((button) => {
      button.addEventListener('click', () => scrollToMedia(button.dataset.mediaId));
    });

    if (gallery && mediaItems.length > 1 && 'IntersectionObserver' in window) {
      const mediaObserver = new IntersectionObserver(
        (entries) => {
          if (!window.matchMedia(MOBILE_QUERY).matches) return;
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (visible) setActiveMediaButton(visible.target.dataset.mediaId);
        },
        { root: gallery, threshold: [0.55, 0.72] }
      );
      mediaItems.forEach((item) => mediaObserver.observe(item));
    }

    if (mobileBuy && addButton && 'IntersectionObserver' in window) {
      const buyObserver = new IntersectionObserver(
        ([entry]) => {
          mobileBuy.classList.toggle('is-hidden', entry.isIntersecting);
        },
        { threshold: 0.35 }
      );
      buyObserver.observe(addButton);
    }

    updateVariant({ updateUrl: false, moveMedia: false });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-fbp-product]').forEach(initProduct);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
